import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Edit, Store } from "lucide-react";
import { toast } from "sonner";

export const MyRestaurants = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/restaurants/owner/me").then(r => setItems(r.data));
  }, []);

  return (
    <div data-testid="my-restaurants">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-black">Restoran Saya</h1>
          <p className="text-muted-foreground">Kelola listing restoranmu</p>
        </div>
        <Link to="/dashboard/restaurants/new">
          <Button className="rounded-full" data-testid="new-restaurant-btn"><Plus className="w-4 h-4 mr-2" />Tambah</Button>
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="warm-card p-12 text-center">
          <Store className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-4">Belum ada restoran. Daftarkan yang pertama!</p>
          <Link to="/dashboard/restaurants/new">
            <Button className="rounded-full">Tambah Restoran</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(r => (
            <div key={r.id} className="warm-card p-4 flex gap-4" data-testid={`my-rest-${r.id}`}>
              <img src={r.cover_image || "https://placehold.co/100"} alt="" className="w-24 h-24 rounded-lg object-cover" />
              <div className="flex-1">
                <h3 className="font-heading font-bold">{r.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">{r.address}</p>
                <p className="text-xs text-muted-foreground">{r.review_count} reviews · ⭐ {r.avg_rating?.toFixed(1) || "—"}</p>
              </div>
              <div className="flex flex-col gap-2">
                <Link to={`/dashboard/restaurants/${r.id}/edit`}>
                  <Button variant="outline" size="sm" className="rounded-full"><Edit className="w-4 h-4 mr-1" />Edit</Button>
                </Link>
                <Link to={`/restaurants/${r.slug}`}>
                  <Button variant="ghost" size="sm" className="rounded-full">Lihat</Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_NAMES = { monday: "Senin", tuesday: "Selasa", wednesday: "Rabu", thursday: "Kamis", friday: "Jumat", saturday: "Sabtu", sunday: "Minggu" };

export const RestaurantForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [menus, setMenus] = useState([]);
  const [form, setForm] = useState({
    name: "", category: "", description: "", address: "", phone: "", instagram: "",
    whatsapp_number: "", reservation_enabled: false, booking_enabled: false,
    location_slug: "gading-serpong",
    google_maps_url: "", lat: "", lng: "", cover_image: "", price_range: "$$",
    holiday_announcement: "", is_open_override: null,
    opening_hours: Object.fromEntries(DAY_KEYS.map(k => [k, "10:00-22:00"])),
  });
  const [newMenu, setNewMenu] = useState({ name: "", price: "", description: "", photo: "" });
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data));
    api.get("/locations").then(r => setLocations(r.data));
    if (isEdit) {
      api.get(`/restaurants/${id}`).then(r => {
        setForm({ ...form, ...r.data, opening_hours: r.data.opening_hours || form.opening_hours });
      });
      api.get(`/restaurants/${id}/menu`).then(r => setMenus(r.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
  };

  const onCoverUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await upload(f);
      setForm({ ...form, cover_image: url });
      toast.success("Foto diupload");
    } catch {
      toast.error("Upload gagal");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null,
      };
      if (isEdit) {
        await api.patch(`/restaurants/${id}`, payload);
        toast.success("Restoran diupdate");
      } else {
        const r = await api.post("/restaurants", payload);
        toast.success("Restoran ditambahkan");
        navigate(`/dashboard/restaurants/${r.data.id}/edit`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const addMenu = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/restaurants/${id}/menu`, {
        ...newMenu,
        price: parseFloat(newMenu.price),
      });
      setNewMenu({ name: "", price: "", description: "", photo: "" });
      const m = await api.get(`/restaurants/${id}/menu`);
      setMenus(m.data);
      toast.success("Menu ditambahkan");
    } catch {
      toast.error("Gagal menambah menu");
    }
  };

  const onMenuPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = await upload(f);
    setNewMenu({ ...newMenu, photo: url });
  };

  const deleteMenu = async (mid) => {
    await api.delete(`/menu/${mid}`);
    setMenus(menus.filter(m => m.id !== mid));
  };

  return (
    <div data-testid="restaurant-form">
      <h1 className="font-heading text-3xl font-black mb-6">{isEdit ? "Edit" : "Tambah"} Restoran</h1>
      <form onSubmit={submit} className="warm-card p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nama Restoran *</Label>
            <Input data-testid="rest-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Kategori *</Label>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="rest-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
              <SelectContent>
                {categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.icon} {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Lokasi / Area *</Label>
          <Select value={form.location_slug} onValueChange={v => setForm({ ...form, location_slug: v })}>
            <SelectTrigger data-testid="rest-location"><SelectValue placeholder="Pilih lokasi" /></SelectTrigger>
            <SelectContent>
              {locations.map(l => <SelectItem key={l.slug} value={l.slug}>{l.name} — {l.city}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Deskripsi</Label>
          <Textarea data-testid="rest-desc" value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>
        <div>
          <Label>Alamat *</Label>
          <Input data-testid="rest-address" required value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Telepon</Label>
            <Input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label>Instagram</Label>
            <Input placeholder="@username" value={form.instagram || ""} onChange={e => setForm({ ...form, instagram: e.target.value })} />
          </div>
          <div>
            <Label>Range Harga</Label>
            <Select value={form.price_range || ""} onValueChange={v => setForm({ ...form, price_range: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="$">$ - Murah</SelectItem>
                <SelectItem value="$$">$$ - Menengah</SelectItem>
                <SelectItem value="$$$">$$$ - Premium</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nomor WhatsApp (untuk Chat & Reservasi)</Label>
            <Input
              data-testid="rest-whatsapp"
              placeholder="62812xxx (format internasional)"
              value={form.whatsapp_number || ""}
              onChange={e => setForm({ ...form, whatsapp_number: e.target.value })}
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-3 cursor-pointer pb-2">
              <input
                type="checkbox"
                data-testid="rest-reservation"
                checked={!!form.reservation_enabled}
                onChange={e => setForm({ ...form, reservation_enabled: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm">Aktifkan tombol "Request Reservasi" (via WhatsApp)</span>
            </label>
          </div>
        </div>

        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="rest-booking-enabled"
              checked={!!form.booking_enabled}
              onChange={e => setForm({ ...form, booking_enabled: e.target.checked })}
              className="w-4 h-4 mt-1"
            />
            <div>
              <span className="text-sm font-bold">Aktifkan Booking Meja Online</span>
              <p className="text-xs text-muted-foreground mt-1">User bisa book meja langsung dari halaman restoran. Kamu konfirmasi/tolak via dashboard "Booking Masuk".</p>
            </div>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Google Maps URL</Label>
            <Input value={form.google_maps_url || ""} onChange={e => setForm({ ...form, google_maps_url: e.target.value })} />
          </div>
          <div>
            <Label>Latitude</Label>
            <Input value={form.lat || ""} onChange={e => setForm({ ...form, lat: e.target.value })} />
          </div>
          <div>
            <Label>Longitude</Label>
            <Input value={form.lng || ""} onChange={e => setForm({ ...form, lng: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Foto Cover</Label>
          {form.cover_image && <img src={form.cover_image} alt="" className="w-full max-w-xs h-40 object-cover rounded-lg mb-2" />}
          <Input type="file" accept="image/*" onChange={onCoverUpload} data-testid="rest-cover-upload" />
        </div>

        <div>
          <Label className="mb-2 block">Jam Buka</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {DAY_KEYS.map(d => (
              <div key={d} className="flex items-center gap-2">
                <span className="w-20 text-sm">{DAY_NAMES[d]}</span>
                <Input
                  data-testid={`hours-${d}`}
                  placeholder="10:00-22:00 atau kosong"
                  value={form.opening_hours[d] || ""}
                  onChange={e => setForm({ ...form, opening_hours: { ...form.opening_hours, [d]: e.target.value } })}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label>Pengumuman Hari Raya / Libur</Label>
          <Textarea
            data-testid="holiday-announcement-input"
            placeholder="Contoh: TUTUP pada Idul Fitri 10-12 April. Buka kembali 13 April."
            value={form.holiday_announcement || ""}
            onChange={e => setForm({ ...form, holiday_announcement: e.target.value })}
          />
        </div>

        <Button type="submit" className="rounded-full" data-testid="save-rest-btn">{isEdit ? "Update" : "Simpan"}</Button>
      </form>

      {isEdit && (
        <div className="warm-card p-6 mt-6">
          <h2 className="font-heading text-xl font-black mb-4">Menu</h2>
          <form onSubmit={addMenu} className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
            <Input required placeholder="Nama menu" value={newMenu.name} onChange={e => setNewMenu({ ...newMenu, name: e.target.value })} data-testid="menu-name" />
            <Input required type="number" placeholder="Harga" value={newMenu.price} onChange={e => setNewMenu({ ...newMenu, price: e.target.value })} data-testid="menu-price" />
            <Input placeholder="Deskripsi" value={newMenu.description} onChange={e => setNewMenu({ ...newMenu, description: e.target.value })} className="md:col-span-2" />
            <Input type="file" accept="image/*" onChange={onMenuPhoto} data-testid="menu-photo" />
            <Button type="submit" className="rounded-full md:col-span-5" data-testid="add-menu-btn"><Plus className="w-4 h-4 mr-2" />Tambah Menu</Button>
          </form>
          <div className="space-y-2">
            {menus.map(m => (
              <div key={m.id} className="flex items-center gap-3 p-3 border border-border rounded-lg" data-testid={`menu-row-${m.id}`}>
                {m.photo && <img src={m.photo} alt="" className="w-12 h-12 rounded object-cover" />}
                <div className="flex-1">
                  <div className="font-bold text-sm">{m.name}</div>
                  <div className="text-xs text-muted-foreground">Rp {m.price.toLocaleString("id-ID")}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => deleteMenu(m.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
